<template>
  <div>
    <SectionSeparatorWrapper headerText="Claims account" :separator="false" headerRequiredFieldsText="Indicates required field">
      <div class="form-section">
        <!-- Info text -->
        <h3 class="h6 sub-heading-underlined">Bank account for receiving benefits</h3>
        <FormField :helperText="refData.claimsAccountCalloutText.informationText" :noLabelOverride="true">
          <div>
            <!-- prettier-ignore -->
            <p>Update the claims account details for <b>{{ memberFullName }}</b>.</p>
          </div>
          <div v-html="refData.claimsAccountInformationText.informationText" />
        </FormField>

        <!-- Question only for PH -->
        <FormField labelText="Would you like to use the same account you nominated for premium payments?" v-if="displayReusePremiumAccountQuestion" required>
          <div class="input-grid">
            <ul class="radio-group large">
              <li v-for="option in bankPaymentSame" :key="option.id">
                <input
                  type="radio"
                  :id="`${option.id}-sameAsPremiumAccount`"
                  :value="option.value"
                  name="sameAsPremiumAccount"
                  v-model="formResponses.isSameAsPremiumAccount"
                  @click="areDetailsDuplicate = false"
                />
                <label :for="`${option.id}-sameAsPremiumAccount`">{{ option.displayValue }}</label>
              </li>
            </ul>
          </div>
        </FormField>
        <!-- Question for partners/dependents -->
        <FormField
          labelText="Would you like to copy the same account details currently nominated for the policy holder?"
          v-if="displayReuseClaimsAccountQuestion"
          :helperText="refData.claimsAccountNotPolicyHolderCalloutText.informationText"
          required
        >
          <div class="input-grid">
            <ul class="radio-group large">
              <li v-for="option in bankPaymentSame" :key="option.id">
                <input
                  type="radio"
                  :id="`${option.id}-sameAsPolicyHolder`"
                  :value="option.value"
                  name="sameAsPolicyHolder"
                  v-model="formResponses.isSameAsPolicyHolder"
                  @click="areDetailsDuplicate = false"
                />
                <label :for="`${option.id}-sameAsPolicyHolder`">{{ option.displayValue }}</label>
              </li>
            </ul>
          </div>
        </FormField>
        <!-- Helper text for limited access members -->
        <SectionSeparatorWrapper :helperText="refData.claimsAccountLimitedAccessMemberCalloutText.informationText" :separator="false" v-if="isLimitedAccessMember" />
        <!-- BSB -->
        <FormField
          labelText="BSB"
          required
          type="text"
          v-model.trim="v$.formResponses.bankBSB.$model"
          class="half-width"
          :error="v$.formResponses.bankBSB.$error"
          :errorMessage="calcBankBSBErrorMessage"
          :placeholder="BSB_MASK"
          v-maska="BSB_MASK"
          :disabled="isAnyQuestionAnsweredYes"
          :overrideValue="overrideBankBsb"
          @blur="checkIfDuplicateDetails()"
        />
        <!-- Account No -->
        <FormField
          labelText="Account number"
          required
          type="number"
          placeholder="Enter account number"
          maxlength="10"
          v-model.trim="v$.formResponses.bankAccountNumber.$model"
          :error="v$.formResponses.bankAccountNumber.$error"
          :errorMessage="calcBankAccountNumberErrorMessage"
          :disabled="isAnyQuestionAnsweredYes"
          @blur="checkIfDuplicateDetails()"
        />
        <!-- Account name -->
        <FormField
          labelText="Account name"
          required
          placeholder="e.g. John Citizen"
          v-model.trim="v$.formResponses.bankAccountName.$model"
          :error="v$.formResponses.bankAccountName.$error"
          :errorMessage="calcBankAccountNameErrorMessage"
          :disabled="isAnyQuestionAnsweredYes"
          @blur="checkIfDuplicateDetails()"
        />

        <FormField>
          <!-- Same details alert (only show if details are duplicate, but not when they're duplicated because of answering YES to a question) -->
          <Alert v-if="areDetailsDuplicate" :message="refData.claimsAccountDuplicateDetailsText.informationText" alertType="info" />

          <!-- Privacy text -->
          <div v-html="refData.claimsAccountPrivacyInformationText.informationText"></div>
        </FormField>
        <!-- Acknowledgement checkbox -->
        <FormField class="align-checkbox-top">
          <div class="field checkbox">
            <input type="checkbox" id="paymentAcknowledgement" v-model="formResponses.claimsAcknowledgement" @change="v$.formResponses.claimsAcknowledgement.$touch()" />
            <label for="paymentAcknowledgement" v-html="addRequiredMark(refData.claimsAccountAuthorisationText.informationText)" />
            <label for="paymentAcknowledgement" class="error" v-if="v$.formResponses.claimsAcknowledgement.$error">{{ ErrorStrings.DECLARATION_MISSING }}</label>
          </div>
        </FormField>
      </div>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { useVuelidate } from '@vuelidate/core';
import { required, numeric, minLength, maxLength, requiredIf } from '@vuelidate/validators';
import { AccessLevel, UpdatePaymentDetailsStepNames } from '@/enums';
import { BSB_VALIDATOR, JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { ErrorStrings } from '@general/js/constants';
import { SectionSeparatorWrapper, FormField, Alert } from '@/components';
import { PaymentMethods } from '@general/js/enums';
import { getAccessLevel, findPolicyHolder } from '@/utils';
import { BSBMask, removeWhitespaceFromString } from '@general/js/utils';
import PaymentDetailsMixin from './PaymentDetailsMixin';

const BSB_MASK = '### - ###';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: UpdatePaymentDetailsStepNames.step2,

  components: {
    SectionSeparatorWrapper,
    FormField,
    Alert,
  },

  mixins: [PaymentDetailsMixin],

  mounted() {
    // this lifecycle hook is only run the first time this component is viewed, second time onwards it'll call `activated()` lifecycle hook because this component is inside <keep-alive>
    this.initialPrefill();
  },

  // this lifecycle hook is run every time this component is viewed (eg. step change)
  activated() {
    // if user has selected Yes to any of the 2 questions, prefill details with updated premium details from step 1
    if (this.isAnyQuestionAnsweredYes) {
      this.prefillDetailsForQuestion();
    }

    // if user goes back to step 1, and changes premium payment account from Bank -> CC, we need to enable fields again(since the question will be hidden) by setting answer as NO(false)
    if (!this.displayReusePremiumAccountQuestion) {
      this.formResponses.isSameAsPremiumAccount = false;
    }
  },

  props: {
    /**
     * This object contains Retrieve accounts response from EAI, which holds `premiumPaymentMethod`, `premiumPaymentAccount`, `claimsAccount` & `mainPolicyHolderClaimsAccount`
     */
    initialData: {
      type: Object,
      required: true,
    },
    /**
     * This object contains ref data fetched from CMS BE
     */
    referenceData: {
      type: Object,
      required: true,
    },
    /**
     * The member (object) to view/update details for
     */
    selectedMember: {
      type: Object,
    },
  },

  data() {
    return {
      overrideBankBsb: '',
      areDetailsDuplicate: false,
      formResponses: {
        isSameAsPremiumAccount: false,
        isSameAsPolicyHolder: false,
        bankBSB: '',
        bankAccountNumber: '',
        bankAccountName: '',
        claimsAcknowledgement: false,
      },
      bankPaymentSame: [
        { id: 'paymentSame', value: true, displayValue: 'Yes' },
        { id: 'paymentNotSame', value: false, displayValue: 'No' },
      ],
      BSB_MASK,
      ErrorStrings,
    };
  },

  validations: {
    formResponses: {
      isSameAsPremiumAccount: {
        required: requiredIf(function () {
          return this.displayReusePremiumAccountQuestion;
        }),
      },
      isSameAsPolicyHolder: {
        required: requiredIf(function () {
          return this.displayReuseClaimsAccountQuestion;
        }),
      },
      bankBSB: {
        required,
        BSB_VALIDATOR,
        minLength: minLength(9),
        maxLength: maxLength(9),
      },
      bankAccountNumber: {
        required,
        numeric,
        minLength: minLength(6),
        maxLength: maxLength(10),
      },
      bankAccountName: {
        required,
        JOIN_NOW_NAME_VALIDATOR,
        maxLength: maxLength(50),
      },
      claimsAcknowledgement: {
        required,
        isSelected: (value) => {
          return value;
        },
      },
    },
  },

  watch: {
    // reset acknowledgement if any of the following data is changed: bsb, account number, account name
    watchClaimsBankDetailsForm() {
      this.formResponses.claimsAcknowledgement = false;
    },
    watchQuestionAnswers(valueArr) {
      // prefill details if selecting Yes to any of the 2 questions
      this.handlePrefills(valueArr);
    },
    formResponses: {
      handler() {
        this.updateClaimsResponsesIntoVuex();
      },
      deep: true,
    },
  },

  computed: {
    calcBankBSBErrorMessage() {
      if (this.v$.formResponses.bankBSB.required.$invalid) {
        return ErrorStrings.BANK_ACCOUNT.BSB_MISSING;
      }
      return ErrorStrings.BANK_ACCOUNT.BSB_GENERIC_MSG;
    },
    calcBankAccountNumberErrorMessage() {
      if (this.v$.formResponses.bankAccountNumber.required.$invalid) {
        return ErrorStrings.BANK_ACCOUNT.MISSING_ACC_NUM;
      }
      return ErrorStrings.BANK_ACCOUNT.ACC_NUM_MIN_LENGTH;
    },
    calcBankAccountNameErrorMessage() {
      if (this.v$.formResponses.bankAccountName.required.$invalid) {
        return ErrorStrings.BANK_ACCOUNT.MISSING_NAME;
      }
      if (this.v$.formResponses.bankAccountName.maxLength.$invalid) {
        return ErrorStrings.BANK_ACCOUNT.NAME_MAX_LENGTH;
      }
      return ErrorStrings.NAME;
    },
    displayReusePremiumAccountQuestion() {
      const isPolicyHolderDetails = this.isPolicyHolderDetails;
      const isPremiumAccOfTypeBank = this.isPremiumAccountOfTypeBank;

      // only show if viewing Policy Holder's details AND if their premium payment account type = Bank
      return isPolicyHolderDetails && isPremiumAccOfTypeBank;
    },
    displayReuseClaimsAccountQuestion() {
      const isNotPolicyHolderDetails = !this.isPolicyHolderDetails;
      const currentAccessLevel = getAccessLevel(this.$route?.params?.policyNumber, this.memberships);
      const policyHolderObject = findPolicyHolder(this.allMembers);
      const allowedAccesses = [AccessLevel.POLICY_HOLDER_ACCESS, AccessLevel.FULL_ACCESS];

      // only show for partners/dependents AND when they have full/PH access AND PH doesn't have keepPrivate=true
      return isNotPolicyHolderDetails && allowedAccesses.includes(currentAccessLevel) && policyHolderObject?.keepPrivateflag !== true;
    },
    watchClaimsBankDetailsForm() {
      return `${this.formResponses.bankBSB}|${this.formResponses.bankAccountNumber}|${this.formResponses.bankAccountName}`;
    },
    watchQuestionAnswers() {
      return [this.formResponses.isSameAsPremiumAccount, this.formResponses.isSameAsPolicyHolder];
    },
    isAnyQuestionAnsweredYes() {
      if (this.formResponses.isSameAsPremiumAccount || this.formResponses.isSameAsPolicyHolder) {
        return true;
      }
      return false;
    },
  },

  methods: {
    handlePrefills(valueArr) {
      if (
        valueArr.some((item) => {
          return item === true;
        })
      ) {
        this.prefillDetailsForQuestion();
      } else {
        this.clearPrefills();
      }
    },

    // Prefill with own claim account details upon first form load
    initialPrefill() {
      // need to clone because we don't want to modify original data, i.e to copy by value and NOT by reference
      const existingClaimsAcc = cloneDeep(this.initialData?.claimsAccount);
      const premiumAccDetailsFromStep1 = cloneDeep(this.paymentDetails?.premiumPaymentAccount);

      // check if PH's claims account details are same as their premium payment account, if yes, default question to Yes
      if (
        existingClaimsAcc &&
        premiumAccDetailsFromStep1 &&
        existingClaimsAcc?.bsb === premiumAccDetailsFromStep1?.bsb &&
        existingClaimsAcc?.accountNumberOrToken === premiumAccDetailsFromStep1?.accountNumberOrToken &&
        existingClaimsAcc?.accountHolderName === premiumAccDetailsFromStep1?.accountHolderName &&
        this.displayReusePremiumAccountQuestion
      ) {
        // setting this true automatically pre-fills account details and disables fields
        this.formResponses.isSameAsPremiumAccount = true;
      } else {
        // else initialize form with own claims details
        this.formResponses.bankBSB = BSBMask(existingClaimsAcc?.bsb);
        this.formResponses.bankAccountNumber = existingClaimsAcc?.accountNumberOrToken;
        this.formResponses.bankAccountName = existingClaimsAcc?.accountHolderName;
      }
    },

    // Prefill details when selecting Yes to any of the 2 questions
    prefillDetailsForQuestion() {
      // if viewing Policy Holder's details, prefill from their own premiumPaymentAccount
      if (this.displayReusePremiumAccountQuestion && this.paymentDetails?.premiumPaymentAccount) {
        // need to clone so that it doesn't alter initialData
        const existingDetails = cloneDeep(this.paymentDetails?.premiumPaymentAccount);

        this.overrideBankBsb = BSBMask(existingDetails?.bsb);
        this.formResponses.bankBSB = BSBMask(existingDetails?.bsb);

        // need to set it again inside nextTick so that it actually gets updated (v-mask bug) (DE2E-7846)
        this.$nextTick(() => {
          this.formResponses.bankBSB = BSBMask(existingDetails?.bsb);
        });
        this.formResponses.bankAccountNumber = existingDetails?.accountNumberOrToken;
        this.formResponses.bankAccountName = existingDetails?.accountHolderName;
        // clear vuelidate validations to clear any errors
        this.v$.$reset();
      }

      // if viewing Partner's/Dependent's details, prefill from their Policy Holder's claims account (mainPolicyHolderCLaimsAccount)
      if (!this.isPolicyHolderDetails && this.initialData?.mainPolicyHolderClaimsAccount?.accountType === PaymentMethods.BANK_ACCOUNT) {
        // need to clone so that it doesn't alter initialData
        const existingDetails = cloneDeep(this.initialData.mainPolicyHolderClaimsAccount);

        this.overrideBankBsb = BSBMask(existingDetails?.bsb);
        this.formResponses.bankBSB = this.overrideBankBsb;
        this.formResponses.bankAccountNumber = existingDetails?.accountNumberOrToken;
        this.formResponses.bankAccountName = existingDetails?.accountHolderName;
        // clear vuelidate validations to clear any errors
        this.v$.$reset();
      }
    },

    clearPrefills() {
      this.formResponses.bankAccountNumber = '';
      this.formResponses.bankAccountName = '';

      // overrideBankBsb is required because for some reason, v-mask interferes with the BSB input field and data isn't cleared even after explicitly clearing it below.
      this.overrideBankBsb = '';
      this.formResponses.bankBSB = '';

      // clear vuelidate validations to clear any errors
      this.v$.$reset();
    },

    updateClaimsResponsesIntoVuex() {
      let claimsAccount;
      if (this.initialData?.claimsAccount) {
        claimsAccount = cloneDeep(this.initialData.claimsAccount);
      } else {
        claimsAccount = {};
      }

      claimsAccount.accountHolderName = this.formResponses.bankAccountName;
      claimsAccount.accountNumberOrToken = this.formResponses.bankAccountNumber;
      claimsAccount.bsb = removeWhitespaceFromString(this.formResponses.bankBSB);
      claimsAccount.directDebitAgreement = this.formResponses.claimsAcknowledgement;
      claimsAccount.accountType = PaymentMethods.BANK_ACCOUNT;

      const claimsExtraData = {
        isSameAsPremiumAccount: this.formResponses.isSameAsPremiumAccount,
        isSameAsPolicyHolder: this.formResponses.isSameAsPolicyHolder,
      };

      const newData = {
        ...this.paymentDetails,
        claimsAccount,
        claimsExtraData,
      };
      // make the update in vuex store for claimsAccount
      this.updatePaymentDetailsInfo(newData); // vuex update
    },

    checkIfDetailsSameAs(objToCheckWith) {
      if (
        removeWhitespaceFromString(this.formResponses.bankBSB) === objToCheckWith.bsb &&
        this.formResponses.bankAccountNumber === objToCheckWith.accountNumberOrToken &&
        this.formResponses.bankAccountName === objToCheckWith.accountHolderName
      ) {
        return true;
      }
      return false;
    },

    // Check if selected NO to any question but still used same account details. This function is invoked by blur(after focus moves away from input) event handler on bsb, name & number fields
    checkIfDuplicateDetails() {
      if (this.displayReusePremiumAccountQuestion) {
        // check if entered details same as PH's premium payment account
        if (this.checkIfDetailsSameAs(this.paymentDetails?.premiumPaymentAccount)) {
          // only duplicate if yes wasn't clicked by user
          if (this.formResponses.isSameAsPremiumAccount === false) {
            // display duplicate details alert
            this.areDetailsDuplicate = true;
          }

          // select Yes to Q automatically
          this.formResponses.isSameAsPremiumAccount = true;
        }
      }
    },
  },
};
</script>
